import { STOCK_MAGNIFIER } from '@diamond/shared/decimal-util-web';
import { Order } from '@diamond/shared/types';
import { promoValidFromToFormat } from '@diamond/shared/utils';

import { ItemInfo } from '../promos/promos';
import { formatDiscountDisplay } from './format-discount-display';
import { VoucherBannerData } from './voucher-banner-data';

type OrderItem = Order['items'][number];

export const extractOrderPromoBannerData = (
  orderItems: OrderItem[]
): VoucherBannerData[] => {
  const banners: VoucherBannerData[] = [];
  const seenPromoIds = new Set<string>();

  orderItems.forEach((item) => {
    const singleFreeGoods = item.promos?.single_free_good_promos || [];
    const bundledFreeGoods = item.promos?.bundled_free_good_promos || [];
    const allFreeGoods = [...singleFreeGoods, ...bundledFreeGoods];

    allFreeGoods.forEach((promo) => {
      // Deduplicate by promo.id - same promo can apply to multiple products
      if (seenPromoIds.has(promo.id)) {
        return;
      }
      seenPromoIds.add(promo.id);
      const images: string[] = [];
      if (promo.product_images_medium?.[0]) {
        images.push(promo.product_images_medium[0]);
      } else if (promo.product_images?.[0]) {
        images.push(promo.product_images[0]);
      }

      const freeQty = (promo.quantity_free_goods || 0) / STOCK_MAGNIFIER;
      const price = promo.price || 0;
      const savings = price * freeQty;

      const itemInfo: ItemInfo = {
        id: promo.product_id || '',
        img: images[0] || '',
        price: price,
        qty: freeQty,
        uom: promo.sales_unit_desc || '',
      };

      const text = promo.mechanism || promo.title || '';

      banners.push({
        type: 'free-item',
        label: 'Gratis',
        text,
        dateRange: promoValidFromToFormat(promo.valid_from, promo.valid_until),
        item: itemInfo,
        images: images.length > 0 ? images : undefined,
        savings: savings > 0 ? savings : undefined,
        voucherCode: promo.promo_code || '',
        voucherId: promo.id,
      });
    });

    const singleDiscountPromos = item.promos?.single_discount_promos || [];
    const bundledDiscountPromos = item.promos?.bundled_discount_promos || [];
    const allDiscountPromos = [
      ...singleDiscountPromos,
      ...bundledDiscountPromos,
    ];

    allDiscountPromos.forEach((promo) => {
      if (seenPromoIds.has(promo.id)) {
        return;
      }
      seenPromoIds.add(promo.id);

      const text = promo.mechanism || '';

      if (promo.is_discount) {
        const discountDisplay =
          promo.deduction_amount != null && promo.deduction_amount > 0
            ? formatDiscountDisplay(promo.deduction_amount, true)
            : '';

        banners.push({
          type: 'discount',
          label: 'Diskon',
          text,
          dateRange: promoValidFromToFormat(
            promo.valid_from,
            promo.valid_until
          ),
          discountDisplay,
          voucherCode: promo.promo_code || '',
          voucherId: promo.id,
        });
      } else {
        banners.push({
          type: 'cashback',
          label: 'Cashback',
          text,
          dateRange: promoValidFromToFormat(
            promo.valid_from,
            promo.valid_until
          ),
          voucherCode: promo.promo_code || '',
          voucherId: promo.id,
        });
      }
    });
  });

  return banners;
};
